import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type BrandFormProps = {
  brandId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type CategoryOption = { id: number; name: string }

const initialValues = {
  name: '',
  descriptions: '',
  active: true,
}

/**
 * Brand form (e.g. Nike, HP, Epson). "Categories" are the machine types
 * (e.g. Printer, POS Terminal) this brand makes — stored as a JSON array
 * on the brand record and used in CreateAssetModal to filter which
 * categories show up once a brand is selected.
 */
export function BrandForm({ brandId, onSuccess, onCancel }: BrandFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!brandId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    setCategoriesLoading(true)
    apiClient
      .get('/master-data/categories', { params: { per_page: 500 } })
      .then((res) => {
        if (cancelled) return
        const rows = Array.isArray(res.data?.data) ? res.data.data : []
        setCategoryOptions(rows.map((r: { id: number; name?: string }) => ({ id: Number(r.id), name: String(r.name ?? r.id) })))
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([])
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadOne = useCallback(async () => {
    if (!brandId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/master-data/brands/${brandId}`)
      const r = data?.data ?? data
      setForm({
        name: r.name ?? '',
        descriptions: r.descriptions ?? '',
        active: r.active !== false,
      })
      let parsedCategories: Array<{ id: number | string }> = []
      try {
        parsedCategories = r.categories ? JSON.parse(r.categories) : []
      } catch {
        parsedCategories = []
      }
      setSelectedCategoryIds(parsedCategories.map((c) => String(c.id)))
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [brandId, showError])

  useEffect(() => {
    if (brandId) {
      loadOne()
    } else {
      setForm(initialValues)
      setSelectedCategoryIds([])
    }
  }, [brandId, loadOne])

  const updateField = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const categoriesPayload = categoryOptions
        .filter((c) => selectedCategoryIds.includes(String(c.id)))
        .map((c) => ({ id: c.id, name: c.name }))

      const payload = {
        name: form.name.trim().toUpperCase(),
        descriptions: form.descriptions.trim() || null,
        categories: JSON.stringify(categoriesPayload),
        attachment_file: '',
        active: form.active,
      }
      if (brandId) {
        await apiClient.put(`/master-data/brands/${brandId}`, payload)
        showSuccess('Success', 'Brand updated successfully')
      } else {
        await apiClient.post('/master-data/brands', payload)
        showSuccess('Success', 'Brand created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value.toUpperCase())}
              placeholder="e.g. EPSON, HP, NIKE"
            />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Categories <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.categories?.length} isRequired>
            <CheckboxGroup value={selectedCategoryIds} onChange={(v) => setSelectedCategoryIds(v as string[])}>
              <Wrap spacing={3}>
                {categoriesLoading && <Box as="span" fontSize="sm">Loading categories...</Box>}
                {!categoriesLoading && categoryOptions.length === 0 && (
                  <Box as="span" fontSize="sm" color="gray.500">No categories yet — create one first.</Box>
                )}
                {categoryOptions.map((c) => (
                  <WrapItem key={c.id}>
                    <Checkbox value={String(c.id)} size="sm">{c.name}</Checkbox>
                  </WrapItem>
                ))}
              </Wrap>
            </CheckboxGroup>
            <FormHelperText>Machine types this brand makes (e.g. Printer, POS Terminal). Determines which models can be assigned to this brand.</FormHelperText>
            <FormErrorMessage>{errors.categories?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.descriptions?.length}>
            <Textarea
              value={form.descriptions}
              onChange={(e) => updateField('descriptions', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.descriptions?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Available for selection on store assets.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={brandId ? 'Saving...' : 'Creating...'}>
            {brandId ? 'Save' : 'Create Brand'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
